import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { AuthUser, SessionResponse } from './auth.models';
import { RuntimeConfigService } from '../config/runtime-config.service';

const TOKEN_KEY = 'gf_access_token';
const USER_KEY = 'gf_current_user';

type LoginResponse = SessionResponse & {
  token?: string;
  accessToken?: string;
};

function normalizeDefaultAppRoute(route: unknown, role = ''): string | undefined {
  if (typeof route !== 'string' || !route.trim()) {
    return undefined;
  }

  const trimmedRoute = route.trim();

  try {
    const baseOrigin = globalThis.location?.origin || 'http://localhost';
    const url = new URL(trimmedRoute, baseOrigin);
    const isLegacyBackofficeRoute =
      url.pathname === '/gfcore/index.html'
      && String(url.searchParams.get('page') || '').trim().toLowerCase() === 'admin';

    if (isLegacyBackofficeRoute) {
      if (role === 'superadmin') {
        return '/app/admin/compagnies';
      }

      if (role === 'admin') {
        return '/app/admin/users';
      }

      return '/app/admin';
    }

    if (url.origin === baseOrigin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return trimmedRoute;
  }

  return trimmedRoute;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly runtimeConfig = inject(RuntimeConfigService);

  private readonly userState = signal<AuthUser | null>(this.readStoredUser());
  private readonly loadedState = signal(false);

  readonly user = computed(() => this.userState());
  readonly isAuthenticated = computed(() => !!this.userState());
  readonly isLoaded = computed(() => this.loadedState());

  private get apiBaseUrl(): string {
    return `${this.runtimeConfig.getConfig().backendBaseUrl}/api/auth`;
  }

  login(identifier: string, password: string): Observable<SessionResponse> {
    return this.http.post<LoginResponse>(`${this.apiBaseUrl}/login`, { identifier, password }).pipe(
      tap((response) => {
        const token = response.token ?? response.accessToken;
        if (token) {
          localStorage.setItem(TOKEN_KEY, token);
        }

        if (response.authenticated && response.user) {
          this.persistUser(this.mapUser(response.user));
        } else if ((response as LoginResponse).user) {
          // Compatibility with token-based login responses that omit authenticated=true.
          this.persistUser(this.mapUser((response as LoginResponse).user as AuthUser));
        }

        this.loadedState.set(true);
      }),
      map((response) => ({
        authenticated: response.authenticated ?? !!response.user,
        user: response.user ? this.mapUser(response.user) : null
      }))
    );
  }

  changePassword(identifier: string, currentPassword: string, newPassword: string): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(`${this.apiBaseUrl}/password/change`, {
      identifier,
      currentPassword,
      newPassword
    });
  }

  ensureSession(): Observable<boolean> {
    return this.http.get<SessionResponse>(`${this.apiBaseUrl}/session`).pipe(
      tap((response) => {
        if (response.authenticated && response.user) {
          this.persistUser(this.mapUser(response.user));
        } else {
          this.clearStoredSession();
        }
        this.loadedState.set(true);
      }),
      map((response) => response.authenticated),
      catchError(() => {
        this.clearStoredSession();
        this.loadedState.set(true);
        return of(false);
      })
    );
  }

  acceptInvitation(token: string, password: string): Observable<{ success: boolean; userId: string }> {
    return this.http.post<{ success: boolean; userId: string }>(`${this.apiBaseUrl}/accept-invitation`, { token, password });
  }

  resetPassword(token: string, password: string): Observable<{ success: boolean; userId: string }> {
    return this.http.post<{ success: boolean; userId: string }>(`${this.apiBaseUrl}/password/reset`, { token, password });
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/logout`, {}).pipe(
      tap(() => this.clearStoredSession()),
      catchError(() => {
        this.clearStoredSession();
        return of(void 0);
      })
    );
  }

  getDefaultAppRoute(user: AuthUser | null = this.userState()): string {
    const normalizedDefaultAppRoute = normalizeDefaultAppRoute(user?.defaultAppRoute, user?.role);

    const hasLegacyForgeAgenticAccess = Boolean(user?.featureAccess?.['forgeAgentic']);
    const hasReviewAccess = Boolean(user?.featureAccess?.['review'] || hasLegacyForgeAgenticAccess);
    const hasAutoReviewAccess = Boolean(user?.featureAccess?.['agenticWorkflow'] || hasLegacyForgeAgenticAccess);

    if (user?.featureAccess?.['improveScenario'] || user?.role === 'superadmin') {
      return '/app/builder/improve';
    }

    if (normalizedDefaultAppRoute) {
      return normalizedDefaultAppRoute;
    }

    if (user?.role === 'admin') {
      return '/app/admin/users';
    }

    if (user?.featureAccess?.['generateScenario']) {
      return '/app/builder/generate';
    }

    if (user?.featureAccess?.['implementScenario']) {
      return '/app/builder/implementation';
    }

    if (user?.featureAccess?.['challengeRequirement']) {
      return '/app/audit/challenge';
    }

    if (user?.featureAccess?.['auditFeature']) {
      return '/app/audit/workspace';
    }

    if (hasReviewAccess) {
      return '/app/ci-cd/review';
    }

    if (hasAutoReviewAccess) {
      return '/app/ci-cd/workflow';
    }

    return '/forbidden';
  }

  getDefaultWorkspaceRoute(user: AuthUser | null = this.userState()): string {
    const normalizedDefaultWorkspaceRoute = normalizeDefaultAppRoute(user?.defaultWorkspaceRoute, user?.role);
    if (
      normalizedDefaultWorkspaceRoute
      && normalizedDefaultWorkspaceRoute !== '/'
      && normalizedDefaultWorkspaceRoute !== '/app/ci-cd/workflow'
      && normalizedDefaultWorkspaceRoute !== '/app/ci-cd/review'
    ) {
      return normalizedDefaultWorkspaceRoute;
    }

    const hasLegacyForgeAgenticAccess = Boolean(user?.featureAccess?.['forgeAgentic']);
    const hasReviewAccess = Boolean(user?.featureAccess?.['review'] || hasLegacyForgeAgenticAccess);
    const hasAutoReviewAccess = Boolean(user?.featureAccess?.['agenticWorkflow'] || hasLegacyForgeAgenticAccess);

    if (user?.featureAccess?.['improveScenario'] || user?.role === 'superadmin') {
      return '/app/builder/improve';
    }

    if (user?.featureAccess?.['generateScenario']) {
      return '/app/builder/generate';
    }

    if (user?.featureAccess?.['implementScenario']) {
      return '/app/builder/implementation';
    }

    if (user?.featureAccess?.['challengeRequirement']) {
      return '/app/audit/challenge';
    }

    if (user?.featureAccess?.['auditFeature']) {
      return '/app/audit/workspace';
    }

    if (hasReviewAccess) {
      return '/app/ci-cd/review';
    }

    if (hasAutoReviewAccess) {
      return '/app/ci-cd/workflow';
    }

    return '/forbidden';
  }

  private persistUser(user: AuthUser): void {
    const mappedUser = this.mapUser(user);
    this.userState.set(mappedUser);
    localStorage.setItem(USER_KEY, JSON.stringify(mappedUser));
  }

  private mapUser(user: Partial<AuthUser> | Record<string, unknown>): AuthUser {
    const source = user as Record<string, unknown> & Partial<AuthUser>;

    const identifier = typeof source.identifier === 'string' && source.identifier.trim()
      ? source.identifier
      : typeof source.username === 'string' && source.username.trim()
        ? source.username
        : typeof source.email === 'string'
          ? source.email
          : '';

    return {
      id: String(source.id ?? ''),
      firstName: typeof source.firstName === 'string' ? source.firstName : '',
      lastName: typeof source.lastName === 'string' ? source.lastName : '',
      identifier,
      email: typeof source.email === 'string' ? source.email : '',
      role: typeof source.role === 'string' ? source.role : '',
      roleName: typeof source.roleName === 'string' ? source.roleName : undefined,
      organizationalRoleKey: typeof source.organizationalRoleKey === 'string' ? source.organizationalRoleKey : undefined,
      organizationalRoleLabel: typeof source.organizationalRoleLabel === 'string' ? source.organizationalRoleLabel : undefined,
      assignedPlanKey: typeof source.assignedPlanKey === 'string' ? source.assignedPlanKey : undefined,
      productAccessPlanKey: typeof source.productAccessPlanKey === 'string' ? source.productAccessPlanKey : undefined,
      productAccessLabel: typeof source.productAccessLabel === 'string' ? source.productAccessLabel : undefined,
      consumesPaidSeat: typeof source.consumesPaidSeat === 'boolean' ? source.consumesPaidSeat : undefined,
      invitationStatus: typeof source.invitationStatus === 'string' ? source.invitationStatus : undefined,
      invitationExpiresAt: typeof source.invitationExpiresAt === 'string' ? source.invitationExpiresAt : null,
      mustChangePassword: typeof source.mustChangePassword === 'boolean' ? source.mustChangePassword : undefined,
      status: typeof source.status === 'string' ? source.status : undefined,
      company: source.company && typeof source.company === 'object' ? source.company as AuthUser['company'] : undefined,
      companyId: typeof source.companyId === 'string' ? source.companyId : undefined,
      featureAccess: source.featureAccess && typeof source.featureAccess === 'object' ? source.featureAccess as Record<string, boolean> : undefined,
      defaultWorkspaceRoute: typeof source.defaultWorkspaceRoute === 'string' ? source.defaultWorkspaceRoute : undefined,
      defaultAppRoute: normalizeDefaultAppRoute(source.defaultAppRoute, typeof source.role === 'string' ? source.role : ''),
      defaultGfcorePage: typeof source.defaultGfcorePage === 'string' ? source.defaultGfcorePage : undefined
    };
  }

  private readStoredUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }

    try {
      return this.mapUser(JSON.parse(raw) as AuthUser);
    } catch {
      return null;
    }
  }

  private clearStoredSession(): void {
    this.userState.set(null);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }
}
